import React, { useEffect, useState, useRef } from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa6';
import { ClientReview, Selection } from './../../../../types';
import './Leaderboards.scss';

type DishCount = {
  item_id: number;
  item_name: string;
  count: number;
  subCounts: Record<string, number>;
};

interface CommentItem {
  text: string;
  category: 'amabilidad' | 'sabor' | 'experiencia';
}

interface Props {
  reviews: ClientReview[];
}

const Leaderboards: React.FC<Props> = ({ reviews }) => {
  const [topFavorites, setTopFavorites] = useState<DishCount[]>([]);
  const [topWorst, setTopWorst] = useState<DishCount[]>([]);
  const [expandedFav, setExpandedFav] = useState<Set<number>>(new Set());
  const [expandedWorst, setExpandedWorst] = useState<Set<number>>(new Set());
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [commentFilter, setCommentFilter] = useState<'all' | 'amabilidad' | 'sabor' | 'experiencia'>('all');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!reviews.length) return;

    const computeTop = (key: 'favorite' | 'worst'): DishCount[] => {
      const map = new Map<number, DishCount>();
      reviews.forEach((r) => {
        const sel = r.object.sabor[key] as Selection | undefined;
        if (!sel) return;
        const entry = map.get(sel.item_id) || {
          item_id: sel.item_id,
          item_name: sel.item_name,
          count: 0,
          subCounts: {},
        };
        entry.count += 1;
        sel.selection_groups.forEach((g) =>
          g.selected_items.forEach((si) => {
            entry.subCounts[si.item_name] = (entry.subCounts[si.item_name] || 0) + 1;
          })
        );
        map.set(sel.item_id, entry);
      });
      return Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, 5);
    };

    setTopFavorites(computeTop('favorite'));
    setTopWorst(computeTop('worst'));

    // Positive comments only (clean, minimalist)
    const cmts: CommentItem[] = [];
    reviews.forEach((r) => {
      if (r.object.amabilidad.rating >= 4 && r.object.amabilidad.comment.trim()) {
        cmts.push({ text: r.object.amabilidad.comment.trim(), category: 'amabilidad' });
      }
      if (r.object.sabor.rating >= 4 && r.object.sabor.comment.trim()) {
        cmts.push({ text: r.object.sabor.comment.trim(), category: 'sabor' });
      }
      if (r.object.experiencia.rating >= 4 && r.object.experiencia.comment.trim()) {
        cmts.push({ text: r.object.experiencia.comment.trim(), category: 'experiencia' });
      }
    });
    setComments(cmts);

    setExpandedFav(new Set());
    setExpandedWorst(new Set());
    setCommentFilter('all');
  }, [reviews]);

  const toggleExpandFav = (id: number) => {
    const s = new Set(expandedFav);
    if (s.has(id)) s.delete(id); else s.add(id);
    setExpandedFav(s);
  };
  const toggleExpandWorst = (id: number) => {
    const s = new Set(expandedWorst);
    if (s.has(id)) s.delete(id); else s.add(id);
    setExpandedWorst(s);
  };

  const totalFav = topFavorites.reduce((acc, d) => acc + d.count, 0) || 1;
  const totalWorst = topWorst.reduce((acc, d) => acc + d.count, 0) || 1;

  const filteredComments = comments.filter((c) => (commentFilter === 'all' ? true : c.category === commentFilter));

  return (
    <div className="leaderboards-container">
      <div className="leaderboards-inner">
        {/* ─── FAVORITES ─── */}
        <div className="dashboard-card leaderboard-card">
          <div className="card-accent-bar" />
          <div className="card-body">
            <h3>Productos favoritos</h3>
            <ul className="leader-list">
              {topFavorites.map((d, i) => {
                const share = Math.round((d.count / totalFav) * 100);
                const itemTotal = d.count || 1;
                return (
                  <li key={`fav-${d.item_id}`} className={`leader-list-item top-${i + 1}`}>
                    <button className="leader-button" onClick={() => toggleExpandFav(d.item_id)}>
                      <span className="rank-badge">{i + 1}</span>
                      <span className="dish-name">{d.item_name}</span>
                      <span className="count-pill">{d.count}<span className="percent">{share}%</span></span>
                      {Object.keys(d.subCounts).length > 0 && (
                        <span className="toggle-icon">{expandedFav.has(d.item_id) ? <FaChevronDown /> : <FaChevronRight />}</span>
                      )}
                    </button>
                    <div className="share-meter" aria-hidden>
                      <div className="meter-fill" style={{ width: `${share}%` }} />
                    </div>
                    {expandedFav.has(d.item_id) && (
                      <ul className="sub-list">
                        {Object.entries(d.subCounts).sort((a,b) => b[1]-a[1]).map(([name, c]) => {
                          const pct = Math.round((c / itemTotal) * 100);
                          return (
                            <li key={`fav-sub-${name}`} className="sub-list-item">
                              <div className="sub-row">
                                <span className="sub-name">{name}</span>
                                <span className="sub-count">{c} <em>{pct}%</em></span>
                              </div>
                              <div className="sub-meter" aria-hidden>
                                <div className="sub-meter-fill" style={{ width: `${pct}%` }} />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* ─── WORST ─── */}
        <div className="dashboard-card leaderboard-card">
          <div className="card-accent-bar" />
          <div className="card-body">
            <h3>Productos menos favoritos</h3>
            <ul className="leader-list">
              {topWorst.map((d, i) => {
                const share = Math.round((d.count / totalWorst) * 100);
                const itemTotal = d.count || 1;
                return (
                  <li key={`worst-${d.item_id}`} className={`leader-list-item top-${i + 1}`}>
                    <button className="leader-button" onClick={() => toggleExpandWorst(d.item_id)}>
                      <span className="rank-badge">{i + 1}</span>
                      <span className="dish-name">{d.item_name}</span>
                      <span className="count-pill">{d.count}<span className="percent">{share}%</span></span>
                      {Object.keys(d.subCounts).length > 0 && (
                        <span className="toggle-icon">{expandedWorst.has(d.item_id) ? <FaChevronDown /> : <FaChevronRight />}</span>
                      )}
                    </button>
                    <div className="share-meter" aria-hidden>
                      <div className="meter-fill" style={{ width: `${share}%` }} />
                    </div>
                    {expandedWorst.has(d.item_id) && (
                      <ul className="sub-list">
                        {Object.entries(d.subCounts).sort((a,b) => b[1]-a[1]).map(([name, c]) => {
                          const pct = Math.round((c / itemTotal) * 100);
                          return (
                            <li key={`worst-sub-${name}`} className="sub-list-item">
                              <div className="sub-row">
                                <span className="sub-name">{name}</span>
                                <span className="sub-count">{c} <em>{pct}%</em></span>
                              </div>
                              <div className="sub-meter" aria-hidden>
                                <div className="sub-meter-fill" style={{ width: `${pct}%` }} />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* ─── COMMENTS (full width) ─── */}
        <div className="dashboard-card comments-card">
          <div className="card-accent-bar" />
          <div className="card-body">
            <div className="comments-header">
              <h4>Comentarios positivos</h4>
              <div className="comments-filters segmented">
                <button className={commentFilter === 'all' ? 'active' : ''} onClick={() => setCommentFilter('all')}>Todos</button>
                <button className={commentFilter === 'amabilidad' ? 'active' : ''} onClick={() => setCommentFilter('amabilidad')}>Servicio</button>
                <button className={commentFilter === 'sabor' ? 'active' : ''} onClick={() => setCommentFilter('sabor')}>Sabor</button>
                <button className={commentFilter === 'experiencia' ? 'active' : ''} onClick={() => setCommentFilter('experiencia')}>Experiencia</button>
              </div>
            </div>
            <div className="comments-list" ref={scrollRef}>
              {filteredComments.length > 0 ? (
                filteredComments.map((c, idx) => (
                  <div key={idx} className={`comment-item`}>
                    <span className="quote-mark">“</span>
                    <span className="comment-text">{c.text}</span>
                  </div>
                ))
              ) : (
                <p className="no-comments">No hay comentarios disponibles</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboards;
